import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { getCheckoutStatus } from "../lib/api";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState({});
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setInfo(data);
          // Starter build projects redirect straight to the delivery dashboard.
          if (data.product_type === "starter_build" && data.created_build_id) {
            navigate(`/custom-app/${data.created_build_id}`);
            return;
          }
          // Preview projects redirect to the project workspace.
          if (data.product_type === "preview_project" && data.created_project_id) {
            navigate(`/preview-project/${data.created_project_id}`);
            return;
          }
          // Continue Building subscription redirects to the build dashboard.
          if (data.product_type === "continue_building") {
            navigate("/continue-building");
            return;
          }
          // Ready-to-Build Startup → detail page shows the transfer + file downloads.
          if (data.product_type === "startup" && data.created_startup_id) {
            navigate(`/apps/${data.created_startup_id}`);
            return;
          }
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, INTERVAL);
    };
    poll();
  }, [navigate]);

  return (
    <Layout>
      <section className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
        {state === "checking" && (
          <div data-testid="checkout-status-checking">
            <Loader2 size={44} className="mx-auto text-[#D4AF37] animate-spin" />
            <h1 className="font-display text-2xl font-bold text-[#0F0F0F] mt-6">Confirming your payment…</h1>
            <p className="text-[#687280] mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="checkout-status-success">
            <CheckCircle2 size={48} className="mx-auto text-green-600" />
            <h1 className="font-display text-3xl font-extrabold text-[#0F0F0F] mt-6">Purchase complete 🎉</h1>
            <p className="text-[#687280] mt-2">
              Thank you! Your order is confirmed. We've emailed your receipt and next steps.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <button
                onClick={() => navigate("/apps")}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-7 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors"
                data-testid="checkout-success-apps-btn"
              >
                Browse more apps <ArrowRight size={18} />
              </button>
              <button
                onClick={() => navigate("/profile")}
                className="inline-flex items-center gap-2 border border-gray-200 text-[#0F0F0F] rounded-full px-7 py-3.5 font-semibold hover:bg-[#F7F7F7] transition-colors"
                data-testid="checkout-success-profile-btn"
              >
                Go to Profile
              </button>
            </div>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="checkout-status-failed">
            <XCircle size={44} className="mx-auto text-red-500" />
            <h1 className="font-display text-2xl font-bold text-[#0F0F0F] mt-6">
              {state === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="text-[#687280] mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, you'll receive confirmation shortly."
                : "Your payment wasn't completed. You can try again anytime."}
            </p>
            <button
              onClick={() => navigate("/apps")}
              className="mt-8 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-8 py-3.5 font-semibold hover:bg-[#1c1c1c] transition-colors"
              data-testid="checkout-success-retry-btn"
            >
              Back to Apps
            </button>
          </div>
        )}
      </section>
    </Layout>
  );
}
